import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Mail, AlertTriangle, Pencil, Paperclip, FileText, Loader2 } from "lucide-react";

import { SmartDatePicker } from "./SmartDatePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/gmail`;

function PendingCard({ card: initial, onDone }) {
  const [card, setCard] = useState(initial);
  const [ticks, setTicks] = useState(() => {
    const t = {}; (initial.proposed_updates || []).forEach(u => { t[u.key] = !!u.preticked; }); return t;
  });
  const [hcConfirm, setHcConfirm] = useState({});
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);     // item 10: acknowledgement shown before the card leaves
  const [pickedDeal, setPickedDeal] = useState("");  // item 3: chosen opportunity when ambiguous

  // Change panel state
  const [showChange, setShowChange] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [chaseInput, setChaseInput] = useState("");
  const [newClientName, setNewClientName] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);
  const [nearMatches, setNearMatches] = useState(null);  // 2-3 closest existing names, shown before creating
  const [nearMatchName, setNearMatchName] = useState(""); // the name that triggered the near-match warning
  const [naChosen, setNaChosen] = useState(false);       // person explicitly marked "not about a client"
  const [attText, setAttText] = useState(null);   // fetched full attachment texts
  const [attBusy, setAttBusy] = useState(false);
  const [showAtt, setShowAtt] = useState(false);
  const attachments = card.attachments || [];

  const fwd = card.forwarded;
  const claimed = fwd && fwd.claimed_original_sender;
  const clientId = (card.proposed_client || {}).client_id || "";
  const decision = card.client_decision || (card.proposed_client ? "match" : "unknown");
  const notAbout = naChosen || decision === "not_client";
  // Reader couldn't establish a client and the person hasn't picked one yet → the card ASKS.
  // A "new" decision is NOT unresolved: it carries a proposed name the person can add on Agree.
  const unresolved = !clientId && !notAbout && decision !== "new";
  const clientName = (card.proposed_client || {}).name || "the client";
  const waitUpdate = (card.proposed_updates || []).find(u => u.key === "wait");
  const chosenDealId = pickedDeal || (card.proposed_deal || {}).opportunity_id || "";
  const dealBlocking = !!card.deal_ambiguous && !chosenDealId && !notAbout;  // item 3: must pick first
  const cannot = card.could_not_establish || [];

  const post = async (path, body) => {
    setBusy(true);
    const r = await fetch(`${API}/pending/${card.pending_id}/${path}`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}) });
    setBusy(false);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(d.detail || "Could not apply"); return false; }
    return d;
  };

  const applyCardUpdate = (updated) => {
    setCard(updated);
    const t = {}; (updated.proposed_updates || []).forEach(u => { t[u.key] = !!u.preticked; });
    setTicks(prev => ({ ...t, ...prev }));  // keep user's ticks, add any new preticks
  };

  // Resolve/create a counterparty through the ONE fail-closed backend path. Returns the new
  // client_id on success, or null if the near-match warning was shown (create blocked) or on error.
  const ensureNewClient = async (name, confirmed = false) => {
    const nm = (name || "").trim();
    if (!nm) return null;
    setCreatingClient(true);
    try {
      const r = await fetch(`${API}/pending/${card.pending_id}/new-client`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: nm, confirmed }) });
      const d = await r.json().catch(() => ({}));
      if (d && d.needs_near_match_ack) {   // fail CLOSED — nothing created, show the warning
        setNearMatches(d.near_matches || []); setNearMatchName(nm); setShowChange(true); return null;
      }
      if (!r.ok) { toast.error(d.detail || "Could not add client"); return null; }
      if (d.card) { applyCardUpdate(d.card); setNaChosen(false); }
      setNearMatches(null); setNewClientName("");
      const rid = d.client_id || (d.card && (d.card.proposed_client || {}).client_id) || null;
      const rname = (d.card && (d.card.proposed_client || {}).name) || nm;
      return rid ? { id: rid, name: rname } : null;
    } catch {
      toast.error("Network error — client not added"); return null;  // fail closed
    } finally { setCreatingClient(false); }
  };

  const agree = async () => {
    if (dealBlocking) { toast.error("Pick which opportunity this is about first"); return; }
    // "new" decision: add the proposed counterparty first (near-match guarded). If the guard
    // blocks (near match shown) or errors, stop here — never confirm without a resolved client.
    let cid = notAbout ? null : clientId;
    let resolvedName = notAbout ? null : clientName;
    if (!cid && !notAbout && decision === "new") {
      const made = await ensureNewClient(card.proposed_new_client_name);
      if (!made) return;
      cid = made.id; resolvedName = made.name;
    }
    const ticked = Object.keys(ticks).filter(k => ticks[k]);
    const hc = Object.keys(hcConfirm).filter(k => hcConfirm[k]);
    const res = await post("confirm", {
      client_id: notAbout ? null : cid, not_about_client: notAbout,
      deal_id: chosenDealId || undefined,
      authorship_confirmed: !!claimed, ticked_updates: ticked,
      high_consequence_confirmed: hc, extra_context: extra });
    if (res) {
      toast.success("Confirmed — updates applied");
      // item 10: hold the card on an acknowledgement until the user dismisses it.
      setDone({ applied: res.applied || ticked, deal_id: chosenDealId, client: resolvedName });
    }
  };
  const notThis = async () => { const r = await post("reject", { reason: extra }); if (r) { toast.success("Dismissed"); onDone && onDone(); } };

  const searchClients = async (q) => {
    setSearchQ(q);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    const r = await fetch(`${API}/clients/search?q=${encodeURIComponent(q)}`, { credentials: "include" });
    if (r.ok) { const d = await r.json(); setSearchResults(d.clients || []); }
  };
  const changeClient = async (cid, cname, na = false) => {
    const r = await post("change", { client_id: na ? null : cid, client_name: cname, not_about_client: na });
    if (r && r.card) { applyCardUpdate(r.card); setNaChosen(na); toast.success(na ? "Set to: not about a client" : "Client changed"); setSearchResults([]); setSearchQ(""); }
  };
  // Create-new-client from the Change panel: goes through the ONE fail-closed backend path
  // (ensureNewClient). `force` = the person acknowledged the near-match and wants it as new.
  const createClient = async (force = false) => {
    const made = await ensureNewClient(force ? nearMatchName : newClientName, force);
    if (made) { setNearMatchName(""); toast.success("Client added"); setSearchResults([]); setSearchQ(""); }
  };
  const changeDeal = async (did) => {
    const r = await post("change", { deal_id: did });
    if (r && r.card) { applyCardUpdate(r.card); toast.success("Deal changed"); }
  };
  const viewAttachments = async () => {
    if (attText) { setShowAtt(s => !s); return; }
    setAttBusy(true);
    try {
      const r = await fetch(`${API}/pending/${card.pending_id}/attachments`, { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Could not load attachment text"); return; }
      setAttText(d.attachments || []);
      setShowAtt(true);
    } finally { setAttBusy(false); }
  };
  const saveChase = async () => {
    if (!chaseInput) return;
    const r = await post("change", { chase_date: chaseInput });
    if (r && r.card) { applyCardUpdate(r.card); toast.success("Chase date changed"); }
  };
  const repropose = async () => {
    const r = await post("repropose", { extra_context: extra });
    if (r && r.card === undefined) {
      // repropose returns fields, not a full card; refetch by patching wait update
      if (r.reproposed) {
        setCard(c => ({ ...c, proposed_updates: (c.proposed_updates || []).map(u => u.key === "wait"
          ? { ...u, chase_date: r.chase_date, chase_days: r.chase_days, chase_label: r.chase_label } : u) }));
        setTicks(t => ({ ...t, wait: true }));
        toast.success(`Chase date re-proposed to ${r.chase_date}`);
      } else { toast.info("No date found in your note"); }
    }
  };

  if (done) {
    return (
      <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200" data-testid={`pending-done-${card.pending_id}`}>
        <div className="text-sm font-medium text-emerald-800 flex items-center gap-1.5">
          <Mail className="w-4 h-4" /> Applied to {done.client || "contact only"}
        </div>
        <div className="mt-1 text-xs text-emerald-700" data-testid={`pending-done-summary-${card.pending_id}`}>
          {(done.applied && done.applied.length)
            ? `Updates written: ${done.applied.map(a => String(a).replace(/^hc:/, "")).join(", ")}`
            : "Recorded — no field updates were ticked."}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs mt-2" disabled={busy}
          data-testid={`pending-dismiss-${card.pending_id}`} onClick={() => onDone && onDone()}>
          Dismiss
        </Button>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={`pending-card-${card.pending_id}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
        <Mail className="w-3.5 h-3.5" /> From {card.sender_name || card.from} · {card.date || "—"}
        {card.duplicate_count > 1 && (
          <span className="ml-1 text-slate-400 bg-slate-100 rounded px-1.5" data-testid={`pending-dupe-count-${card.pending_id}`}
            title="This message was ingested multiple times; shown once, with one decision.">
            {card.duplicate_count} ingestions · shown once
          </span>
        )}
        {card.business_days_open >= 3 && <span className="ml-auto text-rose-600 font-medium">Open {card.business_days_open} business days</span>}
      </div>
      <div className="text-sm font-medium text-slate-800">{card.summary}</div>

      <div className="mt-2 text-xs" data-testid={`pending-match-${card.pending_id}`}>
        <span className="text-slate-500">Client:</span>{" "}
        <b data-testid={`pending-client-label-${card.pending_id}`}>
          {clientId ? (clientName + " (proposed)")
            : notAbout ? "Not about a client"
            : decision === "new" ? `New client: ${card.proposed_new_client_name || "name read from email"} — confirm to add`
            : "Who is this about? Rex couldn't tell"}
        </b>
        {card.proposed_deal && <><span className="ml-2 text-slate-500">Deal:</span> <span className="ml-1">{card.proposed_deal.name} ({card.proposed_deal.stage})</span></>}
      </div>

      {card.deal_ambiguous && !notAbout && (
        <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid={`pending-ambiguous-${card.pending_id}`}>
          <div className="font-medium text-amber-900 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Which opportunity is this about?</div>
          <div className="text-amber-800 mb-1">{clientName} has more than one open deal — pick one before agreeing.</div>
          <select className="h-7 text-xs border rounded px-1 w-full" data-testid={`pending-ambiguous-select-${card.pending_id}`}
            value={pickedDeal} onChange={e => setPickedDeal(e.target.value)}>
            <option value="">— select the right opportunity —</option>
            {(card.deal_alternatives || []).map(d => (
              <option key={d.opportunity_id} value={d.opportunity_id}>{d.name} ({d.stage})</option>
            ))}
          </select>
        </div>
      )}

      {cannot.length > 0 && (
        <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded p-2" data-testid={`pending-cannot-${card.pending_id}`}>
          <div className="font-medium text-slate-600">What Rex couldn't establish</div>
          <ul className="list-disc ml-4 text-slate-600">
            {cannot.map((c, i) => <li key={i} data-testid={`pending-cannot-item-${card.pending_id}-${i}`}>{c}</li>)}
          </ul>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="mt-2 text-xs bg-indigo-50 border border-indigo-200 rounded p-2" data-testid={`pending-attachments-${card.pending_id}`}>
          <div className="font-medium text-indigo-900 flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> Rex read {attachments.length} attachment{attachments.length > 1 ? "s" : ""}
          </div>
          <ul className="mt-1 space-y-1">
            {attachments.map((a, i) => (
              <li key={i} className="flex items-center gap-1.5 flex-wrap" data-testid={`pending-attachment-${card.pending_id}-${i}`}>
                <FileText className="w-3 h-3 text-indigo-500 shrink-0" />
                <span className="text-slate-700 font-medium break-all">{a.filename}</span>
                <span className={`px-1.5 rounded ${a.provenance === "ours" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800"}`}>
                  {a.provenance === "ours" ? "our document" : "from the client"}
                </span>
                {a.value_eligible
                  ? <span className="px-1.5 rounded bg-emerald-100 text-emerald-800" title="May support a deal value">can support value</span>
                  : <span className="px-1.5 rounded bg-slate-100 text-slate-500" title="Context only — never sets our figure">scope / deadline only</span>}
                <span className="text-slate-400">· {a.chars} chars</span>
              </li>
            ))}
          </ul>
          <Button size="sm" variant="ghost" className="h-6 text-xs mt-1 text-indigo-700" disabled={attBusy}
            data-testid={`pending-view-attachments-${card.pending_id}`} onClick={viewAttachments}>
            {attBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            {showAtt ? "Hide extracted text" : "View extracted text"}
          </Button>
          {showAtt && attText && (
            <div className="mt-1 space-y-2" data-testid={`pending-attachment-text-${card.pending_id}`}>
              {attText.map((a, i) => (
                <div key={i} className="bg-white border border-indigo-100 rounded p-2">
                  <div className="font-medium text-slate-600 mb-1">{a.filename}{a.truncated ? " (truncated)" : ""}</div>
                  <pre className="whitespace-pre-wrap break-words text-[11px] text-slate-600 max-h-48 overflow-auto">{a.extracted_text}</pre>
                </div>
              ))}
              {attText.length === 0 && <div className="text-slate-500">No readable text was extracted.</div>}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 space-y-1">
        {(card.proposed_updates || []).map(u => (
          <div key={u.key} className={`text-xs ${u.high_consequence ? "bg-amber-50 border border-amber-200 rounded p-1.5" : ""}`}>
            <label className="flex items-center gap-2">
              <Checkbox checked={!!ticks[u.key]} data-testid={`pending-tick-${card.pending_id}-${u.key}`}
                onCheckedChange={v => setTicks(p => ({ ...p, [u.key]: !!v }))} />
              <span data-testid={`pending-label-${card.pending_id}-${u.key}`}>{u.label}{u.chase_label ? ` — ${u.chase_label}` : ""}</span>
            </label>
            {u.high_consequence && (
              <div className="ml-6 mt-1">
                <div className="text-amber-800">Current: <b>{u.current || "—"}</b> → Proposed: <b>{u.proposed}</b></div>
                {u.provenance && <div className="text-amber-700" data-testid={`pending-provenance-${card.pending_id}-${u.key}`}>Value source: <b>{u.provenance === "outbound_document" ? "our own document" : u.provenance}</b></div>}
                {u.evidence && <div className="text-slate-500 italic">"{u.evidence}"</div>}
                <label className="flex items-center gap-2 mt-1 text-amber-900">
                  <Checkbox checked={!!hcConfirm[u.key]} data-testid={`pending-hc-${card.pending_id}-${u.key}`}
                    onCheckedChange={v => setHcConfirm(p => ({ ...p, [u.key]: !!v }))} />
                  <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Separately confirm this change</span>
                </label>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-2">
        <div className="flex items-center gap-1">
          <Textarea className="text-xs flex-1" rows={2} placeholder="Extra context (e.g. 'they asked us to call back in three weeks')"
            data-testid={`pending-extra-${card.pending_id}`} value={extra} onChange={e => setExtra(e.target.value)} />
        </div>
        <Button size="sm" variant="ghost" className="h-6 text-xs mt-1 text-slate-600" disabled={busy || !extra.trim()}
          data-testid={`pending-repropose-${card.pending_id}`} onClick={repropose}>Re-propose date from note</Button>
      </div>

      {showChange && (
        <div className="mt-2 border rounded p-2 bg-slate-50 space-y-2" data-testid={`pending-change-panel-${card.pending_id}`}>
          <div className="text-xs font-medium text-slate-700">Change match</div>
          <div className="text-xs">
            <div className="text-slate-500 mb-1">Pick another client (alternatives or search):</div>
            <div className="flex flex-wrap gap-1 mb-1">
              {(card.client_alternatives || []).map(a => (
                <Button key={a.client_id} size="sm" variant="outline" className="h-6 text-xs"
                  data-testid={`pending-alt-client-${card.pending_id}-${a.client_id}`}
                  onClick={() => changeClient(a.client_id, a.name)}>{a.name}</Button>
              ))}
              <Button size="sm" variant="outline" className="h-6 text-xs" data-testid={`pending-notabout-${card.pending_id}`}
                onClick={() => changeClient(null, null, true)}>Not about a client</Button>
            </div>
            <Input className="h-7 text-xs" placeholder="Search clients…" value={searchQ}
              data-testid={`pending-client-search-${card.pending_id}`} onChange={e => searchClients(e.target.value)} />
            {searchResults.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {searchResults.map(c => (
                  <Button key={c.client_id} size="sm" variant="secondary" className="h-6 text-xs"
                    data-testid={`pending-search-result-${card.pending_id}-${c.client_id}`}
                    onClick={() => changeClient(c.client_id, c.name)}>{c.name}</Button>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-slate-200">
              <div className="text-slate-500 mb-1">Not in the list? Create a new client:</div>
              <div className="flex gap-1">
                <Input className="h-7 text-xs" placeholder="New client name…" value={newClientName}
                  data-testid={`pending-new-client-name-${card.pending_id}`}
                  onChange={e => setNewClientName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); createClient(); } }} />
                <Button size="sm" className="h-7 text-xs whitespace-nowrap" disabled={busy || creatingClient || !newClientName.trim()}
                  data-testid={`pending-create-client-${card.pending_id}`} onClick={() => createClient(false)}>
                  {creatingClient ? "Creating…" : "Create & use"}
                </Button>
              </div>
              {nearMatches && (
                <div className="mt-1 text-xs bg-amber-50 border border-amber-300 rounded p-2" data-testid={`pending-near-match-${card.pending_id}`}>
                  <div className="text-amber-900 font-medium">Already in the book under a different spelling?</div>
                  <div className="text-amber-800">Adding “{nearMatchName}”. These existing clients look close:</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {nearMatches.map(c => (
                      <Button key={c.client_id} size="sm" variant="secondary" className="h-6 text-xs"
                        data-testid={`pending-near-match-pick-${card.pending_id}-${c.client_id}`}
                        onClick={() => { changeClient(c.client_id, c.name); setNearMatches(null); setNearMatchName(""); setNewClientName(""); }}>
                        {c.name}
                      </Button>
                    ))}
                  </div>
                  <div className="mt-1 flex gap-1">
                    <Button size="sm" className="h-6 text-xs" disabled={creatingClient}
                      data-testid={`pending-create-anyway-${card.pending_id}`} onClick={() => createClient(true)}>
                      No — create “{nearMatchName}” as new
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 text-xs"
                      data-testid={`pending-near-match-cancel-${card.pending_id}`} onClick={() => { setNearMatches(null); setNearMatchName(""); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {(card.deal_alternatives || []).length > 0 && (
            <div className="text-xs">
              <div className="text-slate-500 mb-1">Pick another deal:</div>
              <select className="h-7 text-xs border rounded px-1 w-full" data-testid={`pending-deal-select-${card.pending_id}`}
                value={(card.proposed_deal || {}).opportunity_id || ""} onChange={e => e.target.value && changeDeal(e.target.value)}>
                <option value="">— select —</option>
                {(card.deal_alternatives || []).map(d => (
                  <option key={d.opportunity_id} value={d.opportunity_id}>{d.name} ({d.stage})</option>
                ))}
              </select>
            </div>
          )}
          <div className="text-xs">
            <div className="text-slate-500 mb-1">Edit chase date (currently {waitUpdate ? waitUpdate.chase_date : "—"}):</div>
            <div className="flex gap-1">
              <SmartDatePicker value={chaseInput} onChange={setChaseInput} blockWeekends
                testid={`pending-chase-input-${card.pending_id}`} className="min-w-[150px]" />
              <Button size="sm" className="h-7 text-xs" disabled={busy || !chaseInput}
                data-testid={`pending-chase-save-${card.pending_id}`} onClick={saveChase}>Set date</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <Button size="sm" disabled={busy || dealBlocking || unresolved} data-testid={`pending-agree-${card.pending_id}`} onClick={agree}
          title={unresolved ? "Say who this is about first (pick, create, or mark not about a client)" : undefined}>
          {unresolved ? "Who is this about?"
            : (decision === "new" && !clientId) ? `Add “${card.proposed_new_client_name || "new client"}” & apply`
            : claimed ? `Agree: this reply is from ${claimed} at ${clientName}; apply the ticked updates` : "Agree"}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} data-testid={`pending-change-${card.pending_id}`}
          onClick={() => setShowChange(s => !s)}><Pencil className="w-3 h-3 mr-1" />Change</Button>
        <Button size="sm" variant="outline" disabled={busy} data-testid={`pending-notthis-${card.pending_id}`} onClick={notThis}>Not this</Button>
      </div>
    </div>
  );
}

export function RexInboxTodayCards({ onCount }) {
  const [cards, setCards] = useState([]);
  const load = async () => {
    try {
      const r = await fetch(`${API}/pending`, { credentials: "include" });
      if (r.ok) { const d = await r.json(); setCards(d.cards || []); onCount && onCount((d.cards || []).length); }
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  if (!cards.length) return null;
  return (
    <div className="mb-4" data-testid="from-your-emails">
      <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
        <Mail className="w-4 h-4" /> From your emails <span className="text-xs bg-slate-200 rounded px-1.5">{cards.length}</span>
      </div>
      <div className="space-y-2">
        {cards.map(c => <PendingCard key={c.pending_id} card={c} onDone={load} />)}
      </div>
    </div>
  );
}

export default RexInboxTodayCards;
